<template>
    <!-- Formulario público de huéspedes de los edificios property (/property-huesped).
         Mismo diseño que Mi cuenta (bkCuenta.css: lateral con título en degradé
         y tarjeta a la derecha) + propertyGuestForm.css (prefijo pg-).
         Pedidos de siempre (PropertyGuestController):
           GET  property-form/accommodations   opciones del select
           POST property-form/guests           el formulario tal cual
         Textos: bloque `propertyGuestForm` (6 idiomas). -->
    <div class="pg-page">
        <div class="bk pg">
            <aside class="bk-side pg-side">
                <h1 class="bk-title">{{ $t('propertyGuestForm.intro.title') }} <span class="bk-grad">{{ $t('propertyGuestForm.intro.titleEmphasis') }}</span></h1>
                <p class="bk-subtitle">{{ $t('propertyGuestForm.intro.lede') }}</p>

                <ol class="pg-steps" :aria-label="$t('propertyGuestForm.intro.flowAria')">
                    <li v-for="n in 3" :key="n" :class="{ 'is-done': screen === 'success' || (n === 1 && completos) }">
                        <span class="pg-step-n" aria-hidden="true"><span v-if="screen === 'success' || (n === 1 && completos)" v-html="IC.check"></span><template v-else>{{ n }}</template></span>
                        <div>
                            <b>{{ $t(`propertyGuestForm.intro.step${n}Title`) }}</b>
                            <p>{{ $t(`propertyGuestForm.intro.step${n}Body`) }}</p>
                        </div>
                    </li>
                </ol>
            </aside>

            <section class="pg-main">
                <transition name="pg-swap" mode="out-in">
                    <form v-if="screen === 'form'" key="form" class="pg-card" novalidate @submit.prevent="submit">
                        <header class="pg-card-head">
                            <h2>{{ $t('propertyGuestForm.form.eyebrow') }}</h2>
                            <span class="pg-secure"><span aria-hidden="true" v-html="IC.lock"></span>{{ $t('propertyGuestForm.form.secure') }}</span>
                        </header>

                        <fieldset class="pg-fs" :disabled="sending">
                            <div class="pg-g2">
                                <div :class="['pg-field', { 'is-invalid': errors.nombre }]">
                                    <label for="pg-nombre">{{ $t('propertyGuestForm.form.nombre') }}</label>
                                    <input id="pg-nombre" v-model.trim="form.nombre" type="text" autocomplete="given-name" maxlength="100" :placeholder="$t('propertyGuestForm.form.nombrePh')" :aria-invalid="errors.nombre ? 'true' : null" :aria-describedby="errors.nombre ? 'pg-e-nombre' : null" @input="limpiar('nombre')" />
                                    <small v-if="errors.nombre" id="pg-e-nombre">{{ errors.nombre }}</small>
                                </div>
                                <div :class="['pg-field', { 'is-invalid': errors.apellido }]">
                                    <label for="pg-apellido">{{ $t('propertyGuestForm.form.apellido') }}</label>
                                    <input id="pg-apellido" v-model.trim="form.apellido" type="text" autocomplete="family-name" maxlength="100" :placeholder="$t('propertyGuestForm.form.apellidoPh')" :aria-invalid="errors.apellido ? 'true' : null" :aria-describedby="errors.apellido ? 'pg-e-apellido' : null" @input="limpiar('apellido')" />
                                    <small v-if="errors.apellido" id="pg-e-apellido">{{ errors.apellido }}</small>
                                </div>
                            </div>

                            <div :class="['pg-field', { 'is-invalid': errors.rut_pasaporte }]">
                                <label for="pg-doc">{{ $t('propertyGuestForm.form.documento') }}</label>
                                <input id="pg-doc" v-model.trim="form.rut_pasaporte" type="text" autocomplete="off" maxlength="50" :placeholder="$t('propertyGuestForm.form.documentoPh')" :aria-invalid="errors.rut_pasaporte ? 'true' : null" :aria-describedby="errors.rut_pasaporte ? 'pg-e-doc' : null" @input="limpiar('rut_pasaporte')" />
                                <small v-if="errors.rut_pasaporte" id="pg-e-doc">{{ errors.rut_pasaporte }}</small>
                            </div>

                            <div class="pg-g2">
                                <div :class="['pg-field', { 'is-invalid': errors.correo }]">
                                    <label for="pg-correo">{{ $t('propertyGuestForm.form.correo') }}</label>
                                    <input id="pg-correo" v-model.trim="form.correo" type="email" inputmode="email" autocomplete="email" maxlength="150" :placeholder="$t('propertyGuestForm.form.correoPh')" :aria-invalid="errors.correo ? 'true' : null" :aria-describedby="errors.correo ? 'pg-e-correo' : null" @input="limpiar('correo')" />
                                    <small v-if="errors.correo" id="pg-e-correo">{{ errors.correo }}</small>
                                </div>
                                <div :class="['pg-field', { 'is-invalid': errors.telefono }]">
                                    <label for="pg-tel">{{ $t('propertyGuestForm.form.telefono') }}</label>
                                    <input id="pg-tel" v-model.trim="form.telefono" type="tel" inputmode="tel" autocomplete="tel" maxlength="30" :placeholder="$t('propertyGuestForm.form.telefonoPh')" :aria-invalid="errors.telefono ? 'true' : null" :aria-describedby="errors.telefono ? 'pg-e-tel' : null" @input="limpiar('telefono')" />
                                    <small v-if="errors.telefono" id="pg-e-tel">{{ errors.telefono }}</small>
                                </div>
                            </div>

                            <div class="pg-g2">
                                <div :class="['pg-field', { 'is-invalid': errors.fecha_ingreso }]">
                                    <label for="pg-in">{{ $t('propertyGuestForm.form.fechaIngreso') }}</label>
                                    <input id="pg-in" v-model="form.fecha_ingreso" type="date" :aria-invalid="errors.fecha_ingreso ? 'true' : null" :aria-describedby="errors.fecha_ingreso ? 'pg-e-in' : null" @input="limpiar('fecha_ingreso')" />
                                    <small v-if="errors.fecha_ingreso" id="pg-e-in">{{ errors.fecha_ingreso }}</small>
                                </div>
                                <div :class="['pg-field', { 'is-invalid': errors.fecha_salida }]">
                                    <label for="pg-out">{{ $t('propertyGuestForm.form.fechaSalida') }}</label>
                                    <input id="pg-out" v-model="form.fecha_salida" type="date" :min="form.fecha_ingreso || null" :aria-invalid="errors.fecha_salida ? 'true' : null" :aria-describedby="errors.fecha_salida ? 'pg-e-out' : null" @input="limpiar('fecha_salida')" />
                                    <small v-if="errors.fecha_salida" id="pg-e-out">{{ errors.fecha_salida }}</small>
                                </div>
                            </div>

                            <div :class="['pg-field', { 'is-invalid': errors.property_alojamiento_id }]">
                                <label for="pg-aloj">{{ $t('propertyGuestForm.form.alojamiento') }}</label>
                                <span class="pg-select">
                                    <select id="pg-aloj" v-model="form.property_alojamiento_id" :aria-invalid="errors.property_alojamiento_id ? 'true' : null" :aria-describedby="errors.property_alojamiento_id ? 'pg-e-aloj' : null" @change="limpiar('property_alojamiento_id')">
                                        <option :value="null" disabled>{{ $t('propertyGuestForm.form.alojamientoPh') }}</option>
                                        <option v-for="opt in options" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
                                    </select>
                                    <span aria-hidden="true" v-html="IC.chev"></span>
                                </span>
                                <small v-if="errors.property_alojamiento_id" id="pg-e-aloj">{{ errors.property_alojamiento_id }}</small>
                            </div>
                        </fieldset>

                        <p v-if="generalError" class="pg-error" role="alert"><span aria-hidden="true" v-html="IC.warn"></span>{{ generalError }}</p>

                        <button type="submit" class="bk-btn bk-btn--primary pg-submit" :disabled="sending">
                            <span v-if="sending" class="bk-spinner"></span>{{ sending ? $t('propertyGuestForm.overlay.title') : $t('propertyGuestForm.form.submit') }}
                        </button>
                        <p v-if="sending" class="pg-sending" aria-live="polite">{{ $t('propertyGuestForm.overlay.subtitle') }}</p>
                    </form>

                    <div v-else key="success" ref="exito" class="pg-card pg-done" tabindex="-1" role="status">
                        <span class="pg-done-ic" aria-hidden="true" v-html="IC.check"></span>
                        <span class="pg-badge"><i></i>{{ $t('propertyGuestForm.success.live') }}</span>
                        <h2>{{ $t('propertyGuestForm.success.title') }}</h2>
                        <p class="pg-done-sub">{{ $t('propertyGuestForm.success.subtitle') }}</p>

                        <dl class="pg-pass">
                            <div><dt>{{ $t('propertyGuestForm.success.guest') }}</dt><dd>{{ submitted.nombre }}</dd></div>
                            <div><dt>{{ $t('propertyGuestForm.success.accommodation') }}</dt><dd>{{ submitted.depto }}</dd></div>
                            <div class="pg-pass-dates">
                                <div><dt>{{ $t('propertyGuestForm.success.checkin') }}</dt><dd>{{ submitted.ingreso }}</dd></div>
                                <span aria-hidden="true">→</span>
                                <div><dt>{{ $t('propertyGuestForm.success.checkout') }}</dt><dd>{{ submitted.salida }}</dd></div>
                            </div>
                        </dl>

                        <button type="button" class="bk-btn bk-btn--ghost pg-again" @click="resetForm">{{ $t('propertyGuestForm.success.again') }}</button>
                    </div>
                </transition>
            </section>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

const P = d => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
// Íconos: texto fijo de este archivo, nunca datos del usuario.
const IC = {
    check: P('<path d="M20 6 9 17l-5-5"/>'),
    lock: P('<rect x="4.5" y="10.5" width="15" height="10.5" rx="2.5"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/>'),
    warn: P('<path d="M12 3 2 20h20z"/><path d="M12 10v4M12 17h.01"/>'),
    chev: P('<path d="m6 9 6 6 6-6"/>'),
};
const VACIO = () => ({
    nombre: '', apellido: '', rut_pasaporte: '', correo: '', telefono: '',
    fecha_ingreso: '', fecha_salida: '', property_alojamiento_id: null,
});

export default {
    name: 'PropertyGuestForm',
    data() {
        return {
            IC,
            api: process.env.VUE_APP_API_URL,
            screen: 'form',
            sending: false,
            generalError: '',
            options: [],
            form: VACIO(),
            errors: {},
            submitted: { nombre: '', depto: '', ingreso: '', salida: '' },
        };
    },
    computed: {
        // Para marcar el paso 1 del lateral cuando ya está todo completo.
        completos() { return Object.values(this.form).every(v => v !== '' && v !== null); },
    },
    created() {
        this.loadOptions();
    },
    methods: {
        loadOptions() {
            axios.get(`${this.api}property-form/accommodations`)
                .then(r => { this.options = r.data; })
                .catch(() => { this.generalError = this.$t('propertyGuestForm.errors.loadOptions'); });
        },
        limpiar(k) { if (this.errors[k]) this.$delete(this.errors, k); },
        // Mismas reglas que antes.
        validate() {
            const t = k => this.$t(`propertyGuestForm.errors.${k}`);
            const e = {};
            if (!this.form.nombre) e.nombre = t('nombre');
            if (!this.form.apellido) e.apellido = t('apellido');
            if (!this.form.rut_pasaporte) e.rut_pasaporte = t('documento');
            if (!this.form.correo) e.correo = t('correo');
            else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.form.correo)) e.correo = t('correoInvalid');
            if (!this.form.telefono) e.telefono = t('telefono');
            if (!this.form.fecha_ingreso) e.fecha_ingreso = t('fecha');
            if (!this.form.fecha_salida) e.fecha_salida = t('fecha');
            else if (this.form.fecha_ingreso && this.form.fecha_salida < this.form.fecha_ingreso) e.fecha_salida = t('fechaSalida');
            if (!this.form.property_alojamiento_id) e.property_alojamiento_id = t('alojamiento');
            this.errors = e;
            return Object.keys(e).length === 0;
        },
        formatFecha(value) {
            if (!value) return '';
            const parts = String(value).split('-');
            if (parts.length !== 3) return value;
            const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
            try {
                return d.toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', year: 'numeric' });
            } catch (e) {
                return value;
            }
        },
        submit() {
            if (this.sending) return;
            this.generalError = '';
            if (!this.validate()) {
                this.$nextTick(() => { const el = this.$el.querySelector('.pg-field.is-invalid input, .pg-field.is-invalid select'); if (el) el.focus(); });
                return;
            }
            this.sending = true;
            const chosen = this.options.find(o => o.id === this.form.property_alojamiento_id);
            axios.post(`${this.api}property-form/guests`, this.form)
                .then(() => {
                    this.submitted = {
                        nombre: `${this.form.nombre} ${this.form.apellido}`.trim(),
                        depto: chosen ? chosen.label : '',
                        ingreso: this.formatFecha(this.form.fecha_ingreso),
                        salida: this.formatFecha(this.form.fecha_salida),
                    };
                    this.screen = 'success';
                    // Después de la transición: el aviso recibe el foco y queda a la vista.
                    setTimeout(() => {
                        const el = this.$refs.exito;
                        if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
                    }, 380);
                })
                .catch(err => {
                    this.generalError = (err.response && err.response.data && err.response.data.message) || this.$t('propertyGuestForm.errors.submit');
                })
                .finally(() => { this.sending = false; });
        },
        resetForm() {
            this.form = VACIO();
            this.errors = {};
            this.generalError = '';
            this.screen = 'form';
            this.$nextTick(() => setTimeout(() => { const el = this.$el.querySelector('#pg-nombre'); if (el) el.focus(); }, 380));
        },
    },
};
</script>

<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="./propertyGuestForm.css"></style>
